<script lang="ts" setup>
defineOptions({ name: 'AboutPage' })

import { Icon } from '@/components/Icon'
import {
  companyStats,
  milestones,
  coreAdvantages,
  coreBusiness,
  productSystem,
  philosophy,
  customerLogos
} from '../data/portalData'
import { usePortalState } from '../usePortalState'

const { showTrialModal } = usePortalState()
const router = useRouter()
</script>

<template>
  <div class="about-page">
    <!-- Hero Banner -->
    <div class="hero-banner">
      <div class="hero-circle-1" />
      <div class="hero-circle-2" />
      <div class="hero-circle-3" />
      <div class="hero-dot hero-dot-1" />
      <div class="hero-dot hero-dot-2" />
      <div class="hero-dot hero-dot-3" />
      <div class="hero-dot hero-dot-4" />
      <div class="hero-grid-pattern" />
      <div class="hero-content">
        <div class="hero-tag">
          <Icon icon="lucide:layers" :size="16" />
          国内领先的 IT 高新技术企业
        </div>
        <h1 class="hero-title">深圳紫金山数字技术</h1>
        <p class="hero-subtitle">
          专注于互联网+综合服务，打造国内顶尖的电商生态圈<br />
          100+款自研产品 · 覆盖建站营销、电商交易、AI智能、业务中台、数据中台全场景
        </p>
        <div class="hero-actions">
          <button class="hero-btn-primary" @click="showTrialModal()">
            <Icon icon="lucide:chevron-right" :size="18" />
            免费试用
          </button>
          <button class="hero-btn-outline" @click="showTrialModal()">
            <Icon icon="lucide:phone" :size="18" />
            联系销售
          </button>
        </div>
      </div>
    </div>

    <!-- Stats Dashboard -->
    <div class="stats-wrapper">
      <div class="stats-grid">
        <div v-for="stat in companyStats" :key="stat.label" class="stat-card card-hover">
          <div class="stat-value" :style="{ color: stat.color }">
            {{ stat.value }}<span class="stat-suffix">{{ stat.suffix }}</span>
          </div>
          <p class="stat-label">{{ stat.label }}</p>
        </div>
      </div>
    </div>

    <div class="content-wrap">
      <!-- Company Intro -->
      <section class="intro-section">
        <div class="intro-grid">
          <div class="intro-left">
            <div class="intro-tag">COMPANY PROFILE</div>
            <h2 class="intro-title">
              以技术驱动<span class="brand-highlight">企业数字化</span>转型
            </h2>
            <p class="intro-text">
              深圳市紫金山数字智能有限公司是国内领先的IT高新技术企业，专注于互联网+综合服务，致力于打造国内顶尖的电商生态圈服务。公司深耕企业电商数字化领域，可为广大企业提供全套电商整体解决方案，涵盖电商平台搭建、电商代运营、视觉设计、营销策划、电商培训指导、电商资源整合等多元化配套服务。
            </p>
            <p class="intro-text">
              公司业务产品体系丰富，核心服务包含新零售电商、应用中台、数据中台等各行业的全方位覆盖企业线上数字化建设与电商运营需求，全方位助力企业电商化转型升级。
            </p>
          </div>
          <div class="intro-right">
            <div class="intro-info-card">
              <div class="intro-info-deco-1" />
              <div class="intro-info-deco-2" />
              <div class="intro-info-content">
                <div class="intro-info-label">核心使命</div>
                <div class="intro-info-mission">让每个企业<br />都能拥有数字化能力</div>
                <div class="intro-info-stats-grid">
                  <div class="intro-info-stat-card">
                    <div class="intro-info-stat-value">22</div>
                    <div class="intro-info-stat-desc">产品矩阵</div>
                  </div>
                  <div class="intro-info-stat-card">
                    <div class="intro-info-stat-value">5</div>
                    <div class="intro-info-stat-desc">业务板块</div>
                  </div>
                  <div class="intro-info-stat-card">
                    <div class="intro-info-stat-value">3min</div>
                    <div class="intro-info-stat-desc">极速上线</div>
                  </div>
                  <div class="intro-info-stat-card">
                    <div class="intro-info-stat-value">7×24</div>
                    <div class="intro-info-stat-desc">全天候服务</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Core Advantages -->
      <section class="advantage-section">
        <div class="section-header">
          <div class="section-tag">WHY CHOOSE US</div>
          <h2 class="section-title"> 为什么选择<span class="brand-highlight">紫金山AI</span> </h2>
          <p class="section-subtitle">四大核心优势，为企业数字化转型保驾护航</p>
        </div>
        <div class="advantage-grid">
          <div v-for="item in coreAdvantages" :key="item.title" class="advantage-card card-hover">
            <div
              class="advantage-icon"
              :style="{ background: item.gradient, boxShadow: `0 8px 24px ${item.shadowColor}` }"
            >
              <Icon :icon="item.icon" :size="26" color="#fff" />
            </div>
            <div class="advantage-content">
              <h4 class="advantage-title">{{ item.title }}</h4>
              <p class="advantage-desc">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Milestones Timeline -->
      <section class="milestone-section">
        <div class="section-header">
          <div class="section-tag">MILESTONE</div>
          <h2 class="section-title"> 发展<span class="brand-highlight">历程</span> </h2>
          <p class="section-subtitle">十年深耕，从初创到行业领先</p>
        </div>
        <div class="timeline-wrapper">
          <div class="timeline-line" />
          <div class="timeline-items">
            <div
              v-for="(ms, index) in milestones"
              :key="ms.year"
              class="timeline-item"
              :class="{ 'timeline-right': index % 2 !== 0 }"
            >
              <!-- 偶数：文字在左，右边空白占位 -->
              <div v-if="index % 2 === 0" class="timeline-text timeline-text-left">
                <div class="timeline-year" :style="{ color: ms.color }">{{ ms.year }}</div>
                <h4 class="timeline-title">{{ ms.title }}</h4>
                <p class="timeline-desc">{{ ms.desc }}</p>
              </div>
              <!-- 奇数：左边空白占位，让圆点居中 -->
              <div v-if="index % 2 !== 0" class="timeline-empty" />
              <!-- 圆点始终居中 -->
              <div class="timeline-dot-wrapper">
                <div
                  class="timeline-dot"
                  :style="{ background: ms.color, boxShadow: `0 0 0 4px ${ms.glowColor}` }"
                />
              </div>
              <!-- 奇数：文字在右 -->
              <div v-if="index % 2 !== 0" class="timeline-text timeline-text-right">
                <div class="timeline-year" :style="{ color: ms.color }">{{ ms.year }}</div>
                <h4 class="timeline-title">{{ ms.title }}</h4>
                <p class="timeline-desc">{{ ms.desc }}</p>
              </div>
              <!-- 偶数：右边空白占位 -->
              <div v-if="index % 2 === 0" class="timeline-empty" />
            </div>
          </div>
        </div>
      </section>

      <!-- Core Business -->
      <section class="business-section">
        <div class="section-header">
          <div class="section-tag">CORE BUSINESS</div>
          <h2 class="section-title"> 核心<span class="brand-highlight">业务板块</span> </h2>
          <p class="section-subtitle">涵盖三大核心板块，为企业提供一站式数字化转型服务</p>
        </div>
        <div class="business-grid">
          <div v-for="item in coreBusiness" :key="item.title" class="business-card card-hover">
            <div class="business-deco-circle" :style="{ background: item.lightBg }" />
            <div class="business-inner">
              <div
                class="business-icon"
                :style="{ background: item.gradient, boxShadow: `0 10px 28px ${item.shadowColor}` }"
              >
                <Icon :icon="item.icon" :size="32" color="#fff" />
              </div>
              <h4 class="business-title">{{ item.title }}</h4>
              <p class="business-desc">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Product System -->
      <section class="product-system-section">
        <div class="section-header">
          <div class="section-tag">PRODUCT SYSTEM</div>
          <h2 class="section-title"> 产品<span class="brand-highlight">体系</span> </h2>
          <p class="section-subtitle">三大产品矩阵，全面覆盖企业数字化全场景需求</p>
        </div>
        <div class="product-system-grid">
          <div
            v-for="item in productSystem"
            :key="item.title"
            class="product-system-card card-hover"
          >
            <div class="product-system-icon" :style="{ background: item.bg }">
              <Icon :icon="item.icon" :size="26" :color="item.color" />
            </div>
            <div class="product-system-content">
              <h4 class="product-system-title">{{ item.title }}</h4>
              <p class="product-system-desc">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Philosophy -->
      <section class="philosophy-section">
        <div class="philosophy-deco-ring-1" />
        <div class="philosophy-deco-ring-2" />
        <div class="philosophy-inner">
          <div class="section-tag philosophy-tag">OUR PHILOSOPHY</div>
          <h2 class="philosophy-title">我们的理念</h2>
          <p class="philosophy-subtitle">始终立足客户真实需求，持续迭代产品与服务体系</p>
          <div class="philosophy-grid">
            <div v-for="item in philosophy" :key="item.title" class="philosophy-card card-hover">
              <div class="philosophy-icon-circle">
                <Icon :icon="item.icon" :size="28" color="currentColor" />
              </div>
              <h4 class="philosophy-card-title">{{ item.title }}</h4>
              <p class="philosophy-card-desc">{{ item.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Contact CTA -->
      <section class="contact-cta">
        <div class="contact-cta-tag">CONTACT US</div>
        <h2 class="contact-cta-title"> 联系<span class="brand-highlight">我们</span> </h2>
        <p class="contact-cta-desc">期待与您的合作，共同开启数字化新篇章</p>
        <div class="contact-cta-grid">
          <div class="contact-cta-card card-hover">
            <div class="contact-cta-icon" style="background: #ebf5ff">
              <Icon icon="lucide:map-pin" :size="24" color="#0066FF" />
            </div>
            <h4 class="contact-cta-card-title">公司总部</h4>
            <p class="contact-cta-card-desc">广东省深圳市宝安区金海路12号<br />汇潮科技大厦2605</p>
          </div>
          <div class="contact-cta-card card-hover">
            <div class="contact-cta-icon" style="background: #ecfdf5">
              <Icon icon="lucide:phone" :size="24" color="#00B96B" />
            </div>
            <h4 class="contact-cta-card-title">客服热线</h4>
            <p class="contact-cta-card-desc">400-646-5288<br />kefu@zjsai.com</p>
          </div>
          <div class="contact-cta-card card-hover">
            <div class="contact-cta-icon" style="background: #fffbeb">
              <Icon icon="lucide:briefcase" :size="24" color="#FF9500" />
            </div>
            <h4 class="contact-cta-card-title">加入我们</h4>
            <p class="contact-cta-card-desc">hr@zjsai.com<br />携手共创数字化未来</p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.about-page {
  padding-top: 64px;
}

.brand-highlight {
  color: #0066ff;
}

// Hero
.hero-banner {
  height: 584px; // 520 + 64, 向上延伸覆盖 navbar-spacer
  margin-top: -64px;
  background: linear-gradient(160deg, #001b5e 0%, #002d8a 30%, #0066ff 70%, #3388ff 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-circle-1 {
  position: absolute;
  top: -120px;
  right: -80px;
  width: 500px;
  height: 500px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 50%;
}

.hero-circle-2 {
  position: absolute;
  bottom: -160px;
  left: -60px;
  width: 400px;
  height: 400px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 50%;
}

.hero-circle-3 {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 700px;
  height: 700px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 50%;
}

.hero-dot {
  position: absolute;
  width: 6px;
  height: 6px;
  background: rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  animation: float-dot 4s ease-in-out infinite;

  &.hero-dot-1 {
    top: 15%;
    left: 10%;
  }
  &.hero-dot-2 {
    top: 25%;
    right: 15%;
    width: 4px;
    height: 4px;
    animation-delay: 1s;
  }
  &.hero-dot-3 {
    bottom: 30%;
    left: 20%;
    width: 5px;
    height: 5px;
    animation-delay: 2s;
  }
  &.hero-dot-4 {
    top: 60%;
    right: 25%;
    width: 3px;
    height: 3px;
    animation-delay: 0.5s;
  }
}

@keyframes float-dot {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

.hero-grid-pattern {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px);
  background-size: 40px 40px;
  opacity: 0.5;
}

.hero-content {
  position: relative;
  z-index: 3;
  text-align: center;
  color: #fff;
  max-width: 860px;
  padding: 64px 32px 0;
}

.hero-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 50px;
  padding: 6px 20px;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 24px;
  letter-spacing: 0.5px;
}

.hero-title {
  font-size: 52px;
  font-weight: 900;
  margin-bottom: 20px;
  line-height: 1.2;
  letter-spacing: -1px;
}

.hero-subtitle {
  font-size: 20px;
  opacity: 0.85;
  line-height: 1.7;
  margin-bottom: 32px;
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
}

.hero-actions {
  display: flex;
  gap: 14px;
  justify-content: center;
  flex-wrap: wrap;
}

.hero-btn-primary {
  height: 48px;
  padding: 0 32px;
  border-radius: 12px;
  background: #fff;
  color: #0066ff;
  font-size: 15px;
  font-weight: 700;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  }
}

.hero-btn-outline {
  height: 48px;
  padding: 0 32px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  border: 1px solid rgba(255, 255, 255, 0.25);
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: rgba(255, 255, 255, 0.22);
    transform: translateY(-2px);
  }
}

// Stats
.stats-wrapper {
  max-width: 1100px;
  margin: -56px auto 0;
  padding: 0 24px;
  position: relative;
  z-index: 10;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.stat-card {
  background: #fff;
  border-radius: 16px;
  padding: 28px 20px;
  text-align: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  border: 1px solid #f3f4f6;
}

.stat-value {
  font-size: 36px;
  font-weight: 900;
  line-height: 1;
  margin-bottom: 8px;
}

.stat-suffix {
  font-size: 20px;
}

.stat-label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

// Content
.content-wrap {
  padding: 80px 24px 64px;
  max-width: 1100px;
  margin: 0 auto;
}

.section-header {
  text-align: center;
  margin-bottom: 48px;
}

.section-tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  color: #0066ff;
  text-transform: uppercase;
  letter-spacing: 2px;
  background: #ebf5ff;
  padding: 4px 14px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.section-title {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 12px;
  color: #111827;
}

.section-subtitle {
  font-size: 15px;
  color: #6b7280;
  max-width: 600px;
  margin: 0 auto;
}

// Intro
.intro-section {
  margin-bottom: 80px;
}

.intro-grid {
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 48px;
  align-items: center;
}

.intro-tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  color: #0066ff;
  text-transform: uppercase;
  letter-spacing: 2px;
  background: #ebf5ff;
  padding: 4px 14px;
  border-radius: 4px;
  margin-bottom: 20px;
}

.intro-title {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 20px;
  line-height: 1.3;
  color: #111827;
}

.intro-text {
  font-size: 15px;
  line-height: 2;
  color: #4b5563;
  text-indent: 2em;
  margin-bottom: 14px;
}

.intro-info-card {
  background: linear-gradient(135deg, #0066ff, #0044cc);
  border-radius: 24px;
  padding: 40px 32px;
  color: #fff;
  position: relative;
  overflow: hidden;
}

.intro-info-deco-1 {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 160px;
  height: 160px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 24px;
  transform: rotate(15deg);
}

.intro-info-deco-2 {
  position: absolute;
  bottom: -24px;
  left: -24px;
  width: 100px;
  height: 100px;
  border: 2px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  transform: rotate(-10deg);
}

.intro-info-content {
  position: relative;
  z-index: 2;
}

.intro-info-label {
  font-size: 14px;
  font-weight: 500;
  opacity: 0.8;
  margin-bottom: 4px;
}

.intro-info-mission {
  font-size: 22px;
  font-weight: 800;
  margin-bottom: 28px;
  line-height: 1.4;
}

.intro-info-stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.intro-info-stat-card {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 18px 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.intro-info-stat-value {
  font-size: 24px;
  font-weight: 800;
  margin-bottom: 4px;
}

.intro-info-stat-desc {
  font-size: 12px;
  opacity: 0.8;
}

// Advantages
.advantage-section {
  margin-bottom: 80px;
}

.advantage-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

.advantage-card {
  background: #fff;
  border: 1px solid #f3f4f6;
  border-radius: 20px;
  padding: 36px 32px;
  display: flex;
  gap: 24px;
  align-items: flex-start;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.advantage-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.advantage-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #1f2937;
}

.advantage-desc {
  font-size: 14px;
  line-height: 1.8;
  color: #6b7280;
}

// Milestones
.milestone-section {
  margin-bottom: 80px;
}

.timeline-wrapper {
  position: relative;
  max-width: 800px;
  margin: 0 auto;
}

.timeline-line {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, transparent, #0066ff 10%, #0066ff 90%, transparent);
  transform: translateX(-50%);
}

.timeline-items {
  display: flex;
  flex-direction: column;
  gap: 40px;
  position: relative;
}

.timeline-item {
  display: flex;
  align-items: flex-start;
  gap: 40px;
}

.timeline-text {
  flex: 1;
}

.timeline-text-left {
  text-align: right;
  padding-right: 50px;
}

.timeline-text-right {
  padding-left: 50px;
}

.timeline-year {
  font-size: 24px;
  font-weight: 900;
  margin-bottom: 6px;
}

.timeline-title {
  font-size: 17px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 6px;
}

.timeline-desc {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.7;
}

.timeline-dot-wrapper {
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  margin-top: 4px;
}

.timeline-dot {
  width: 16px;
  height: 16px;
  border: 3px solid #fff;
  border-radius: 50%;
}

.timeline-empty {
  flex: 1;
}

// Core Business
.business-section {
  margin-bottom: 80px;
}

.business-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.business-card {
  background: #fff;
  border: 1px solid #f3f4f6;
  border-radius: 20px;
  padding: 40px 28px;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.business-deco-circle {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  opacity: 0.4;
}

.business-inner {
  position: relative;
  z-index: 1;
}

.business-icon {
  width: 68px;
  height: 68px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.business-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #1f2937;
}

.business-desc {
  font-size: 14px;
  line-height: 1.8;
  color: #6b7280;
}

// Product System
.product-system-section {
  margin-bottom: 80px;
}

.product-system-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.product-system-card {
  background: #fff;
  border: 1px solid #f3f4f6;
  border-radius: 20px;
  padding: 32px;
  display: flex;
  gap: 20px;
  align-items: flex-start;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.product-system-icon {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.product-system-title {
  font-size: 17px;
  font-weight: 700;
  margin-bottom: 10px;
  color: #1f2937;
}

.product-system-desc {
  font-size: 14px;
  line-height: 1.7;
  color: #6b7280;
}

// Philosophy
.philosophy-section {
  margin-bottom: 80px;
  background:
    radial-gradient(circle at 30% 50%, rgba(0, 102, 255, 0.15), transparent 50%),
    radial-gradient(circle at 70% 30%, rgba(0, 185, 107, 0.1), transparent 40%),
    linear-gradient(160deg, #001b5e 0%, #002466 40%, #0066ff 100%);
  border-radius: 24px;
  padding: 64px 40px;
  color: #fff;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.philosophy-deco-ring-1 {
  position: absolute;
  top: -100px;
  left: -100px;
  width: 250px;
  height: 250px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 50%;
}

.philosophy-deco-ring-2 {
  position: absolute;
  bottom: -80px;
  right: -80px;
  width: 200px;
  height: 200px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 50%;
}

.philosophy-inner {
  position: relative;
  z-index: 2;
}

.philosophy-tag {
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.12);
}

.philosophy-title {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 12px;
}

.philosophy-subtitle {
  font-size: 15px;
  opacity: 0.8;
  margin-bottom: 48px;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
}

.philosophy-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
  max-width: 900px;
  margin: 0 auto;
}

.philosophy-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 36px 24px;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.philosophy-icon-circle {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.philosophy-card-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 12px;
}

.philosophy-card-desc {
  font-size: 14px;
  line-height: 1.8;
  opacity: 0.8;
}

// Logos
.logo-section {
  margin-bottom: 80px;
}

.logo-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 28px 36px;
}

.logo-card {
  background: #fff;
  border: 1px solid #f3f4f6;
  border-radius: 14px;
  padding: 18px 28px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.logo-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 800;
  font-size: 14px;
}

.logo-name {
  font-size: 14px;
  font-weight: 600;
  color: #4b5563;
}

// Contact CTA
.contact-cta {
  text-align: center;
  max-width: 960px;
  margin: 0 auto;
  padding-top: 48px;
  border-top: 2px solid #f3f4f6;
}

.contact-cta-tag {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  color: #0066ff;
  text-transform: uppercase;
  letter-spacing: 2px;
  background: #ebf5ff;
  padding: 4px 14px;
  border-radius: 4px;
  margin-bottom: 16px;
}

.contact-cta-title {
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 12px;
  color: #111827;
}

.contact-cta-desc {
  font-size: 15px;
  color: #6b7280;
  margin-bottom: 40px;
}

.contact-cta-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 40px;
}

.contact-cta-card {
  background: #fff;
  border: 1px solid #f3f4f6;
  border-radius: 18px;
  padding: 32px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.contact-cta-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.contact-cta-card-title {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 6px;
  color: #1f2937;
}

.contact-cta-card-desc {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.7;
}

.contact-cta-footer {
  font-size: 13px;
  color: #9ca3af;
  line-height: 1.8;
}

.card-hover {
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.08);
  }
}

// Responsive
@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .intro-grid {
    grid-template-columns: 1fr;
  }

  .advantage-grid {
    grid-template-columns: 1fr;
  }

  .business-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .product-system-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .philosophy-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .contact-cta-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 32px;
  }

  .hero-subtitle {
    font-size: 14px;
  }

  .hero-actions {
    flex-direction: column;
    align-items: center;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .business-grid {
    grid-template-columns: 1fr;
  }

  .product-system-grid {
    grid-template-columns: 1fr;
  }

  .philosophy-grid {
    grid-template-columns: 1fr;
  }

  .timeline-line {
    left: 20px;
  }

  .timeline-item {
    gap: 0;
    padding-left: 40px;

    &.timeline-right {
      flex-direction: row;
    }
  }

  .timeline-text-left {
    text-align: left;
    padding-right: 0;
  }

  .timeline-dot-wrapper {
    position: absolute;
    left: 12px;
  }

  .timeline-empty {
    display: none;
  }

  .contact-cta-grid {
    grid-template-columns: 1fr;
  }

  .logo-grid {
    gap: 16px;
  }
}
</style>
